// WeatherHistoryPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Table, Button, ConfigProvider, Select, message, Card, Tag, Input, Tooltip } from 'antd';
import SideMenu from '../components/SideMenu';
import {
  fetchEventForecasts,
  updateForecastAccumulation,
  fetchForecastPDF,
  syncForecastPDF
} from '../api';
import { useEvents } from '../context/EventContext';
import { useConstants } from '../context/ConstantsContext';
import '../styles/ButtonStyles.css'
import '../styles/CustomCheckbox.css'
import { DownloadOutlined, CheckOutlined, CloseOutlined, WarningOutlined, SyncOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';

const EventHistoryPage = () => {
  const constants = useConstants();
  const { allEvents, selectedEventId, selectedEvent, selectEvent, isLoading: eventsLoading } = useEvents();
  const [forecasts, setForecasts] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [syncingForecasts, setSyncingForecasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Initialize event selection from context
  useEffect(() => {
    if (allEvents.length > 0 && !selectedEventId) {
      // Select first event if none selected
      selectEvent(allEvents[0].event_id);
    }
  }, [allEvents, selectedEventId, selectEvent]);

  const loadForecasts = async (eventId) => {
    setLoading(true);
    try {
      const forecast_data = await fetchEventForecasts(eventId);

      const sortedForecasts = forecast_data.sort((a, b) => {
        if (a.country !== b.country) {
          return a.country.localeCompare(b.country);
        }
        if (a.province_state !== b.province_state) {
          return a.province_state.localeCompare(b.province_state);
        }
        return a.station_name.localeCompare(b.station_name);
      });

      setForecasts(sortedForecasts);
    } catch (error) {
      console.error('Error fetching forecasts:', error);
      messageApi.error('Failed to fetch forecasts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEventId) {
      loadForecasts(selectedEventId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  const handleEventChange = (eventId) => {
    selectEvent(eventId);
  };

  const editAccumulation = (record) => {
    setEditingKey(record.forecast_id);
    setEditingValue(record.actual_accum);
  };

  const cancelEditAccumulation = () => {
    setEditingKey(null);
    setEditingValue(null);
  };

  const saveEditAccumulation = async (record) => {
    try {
      await updateForecastAccumulation(record.forecast_id, editingValue);
      const updatedForecasts = forecasts.map((item) =>
        item.forecast_id === record.forecast_id
          ? { ...item, actual_accum: editingValue }
          : item
      );
      setForecasts(updatedForecasts);
      messageApi.success('Forecast actual accumulation updated successfully');
      cancelEditAccumulation();
    } catch (error) {
      messageApi.error('Failed to update forecast actual accumulation');
      cancelEditAccumulation();
    }
  };


  const handleForecastSync = async (forecastId) => {
    setSyncingForecasts((prev) => [...prev, forecastId]);
    try {
      const response = await syncForecastPDF(forecastId);
      if (response.message) {
        messageApi.success(response.message);
      } else {
        messageApi.success('Forecast PDF synced successfully');
      }
      if (selectedEventId) {
        loadForecasts(selectedEventId);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch forecast PDF from the Google Drive';
      messageApi.error(errorMessage);
      console.error('Error syncing forecast PDF:', error);
    } finally {
      setSyncingForecasts((prev) => prev.filter((id) => id !== forecastId));
    }
  };


  const forecastColumns = [
    {
      title: 'Weather Station Name',
      dataIndex: 'station_name',
      key: 'station_name',
      width: '15%',
    },
    {
      title: 'Forecast',
      dataIndex: 'forecast',
      key: 'forecast',
      width: '20%',
    },
    {
      title: 'Forecast Low',
      dataIndex: 'low_temp',
      key: 'low_temp',
      width: '10%',
      render: (low_temp, record) => (
        <span>
          {low_temp}°{record.country === constants.country.canada ? 'C' : 'F'}
        </span>
      ),
    },
    {
      title: 'Forecast High',
      dataIndex: 'high_temp',
      key: 'high_temp',
      width: '10%',
      render: (high_temp, record) => (
        <span>
          {high_temp}°{record.country === constants.country.canada ? 'C' : 'F'}
        </span>
      ),
    },
    {
      title: 'Forecast Accum',
      dataIndex: 'forecast_accum',
      key: 'forecast_accum',
      width: '10%',
      render: (forecast_accum, record) => (
        <span>
          {forecast_accum} {record.country === constants.country.canada ? 'cm' : 'inches'}
        </span>
      ),
    },
    {
      title: (
        <span>
          Actual Accum{' '}
          <Tooltip title="This value will be used for Purchase Order and Invoice amount calculations for Per-CM/Per-Inch contract types">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'actual_accum',
      key: 'actual_accum',
      width: '15%',
      align: 'center',
      render: (actual_accum, record) =>
        editingKey === record.forecast_id ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Input
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              style={{ width: '60%', marginRight: '8px' }}
              autoFocus
            />
            <CheckOutlined
              onClick={() => saveEditAccumulation(record)}
              style={{ color: 'green', cursor: 'pointer', marginRight: '8px' }}
            />
            <CloseOutlined
              onClick={cancelEditAccumulation}
              style={{ color: 'red', cursor: 'pointer' }}
            />
          </div>
        ) : actual_accum !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span
              onClick={() => editAccumulation(record)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              {actual_accum} {record.country === constants.country.canada ? 'cm' : 'inches'}
              <EditOutlined style={{ fontSize: '0.8em', color: 'gray' }} />
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tag color="red" onClick={() => editAccumulation(record)}>
              Weather Data Incomplete
            </Tag>
          </div>
        ),
    },
    {
      title: (
        <span>
          Forecast PDF{' '}
          <Tooltip title="Used for service notification emails if no alternative forecast PDF is selected">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'forecast_id',
      key: 'forecast_pdf',
      width: '18%',
      align: 'left',
      render: (forecast_id, record) => {
        const getSourceLabel = (source) => {
          if (!source) return 'Unknown';
          const sourceMap = {
            'EC': 'Environment Canada',
            'NWS': 'National Weather Service',
            'WN': 'Weather Network'
          };
          return sourceMap[source] || source;
        };

        const getTypeLabel = (type, source) => {
          if (!type) return 'Current';
          if (type === 'hourly') {
            return source === 'WN' ? 'Hourly' : 'Hourly';
          }
          return source === 'WN' ? '7-day' : 'Current';
        };

        const sourceLabel = getSourceLabel(record.pdf_source);
        const typeLabel = getTypeLabel(record.pdf_type, record.pdf_source);

        const downloadPDF = async () => {
          try {
            const pdfBlob = await fetchForecastPDF(forecast_id);
            const pdfUrl = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `${record.station_name} Forecast ${record.date}.pdf`;
            link.click();
            window.URL.revokeObjectURL(pdfUrl);
          } catch (error) {
            messageApi.error('Failed to download the forecast PDF');
            console.error('Error fetching forecast PDF:', error);
          }
        };

        return (
          <div style={{ fontSize: '13px' }}>
            <div style={{ fontWeight: 500, marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              {record.station_name}
              {record.pdf_available ? (
                <Tooltip title="Download forecast PDF">
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={downloadPDF}
                    style={{ padding: '0 4px' }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    <>
                      The forecast PDF is unavailable. <br />
                      Please upload the PDF to the Google Drive at: <br />
                      {record.pdf_path}
                    </>
                  }
                >
                  <WarningOutlined style={{ color: 'goldenrod', fontSize: '14px' }} />
                </Tooltip>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
              <div>{sourceLabel}</div>
              <div>{record.pdf_time} • {typeLabel}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span>
          Alternative Forecast PDF{' '}
          <Tooltip title="When selected, this forecast is used for service notification emails instead of the original forecast">
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#999' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'alternate_forecast',
      key: 'alternate_forecast_pdf',
      width: '18%',
      align: 'left',
      render: (alternate_forecast, record) => {
        if (!alternate_forecast) {
          return <span style={{ color: '#999', fontSize: '12px' }}>None</span>;
        }

        const altForecast = forecasts.find(f => f.forecast_id === alternate_forecast);
        
        if (!altForecast) {
          return <span style={{ color: '#999', fontSize: '12px' }}>Unknown</span>;
        }

        const getSourceLabel = (source) => {
          if (!source) return 'Unknown';
          const sourceMap = {
            'EC': 'Environment Canada',
            'NWS': 'National Weather Service',
            'WN': 'Weather Network'
          };
          return sourceMap[source] || source;
        };

        const getTypeLabel = (type, source) => {
          if (!type) return 'Current';
          if (type === 'hourly') {
            return source === 'WN' ? 'Hourly' : 'Hourly';
          }
          return source === 'WN' ? '7-day' : 'Current';
        };

        const sourceLabel = getSourceLabel(altForecast.pdf_source);
        const typeLabel = getTypeLabel(altForecast.pdf_type, altForecast.pdf_source);

        const downloadPDF = async () => {
          try {
            const pdfBlob = await fetchForecastPDF(alternate_forecast);
            const pdfUrl = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = `${altForecast.station_name} Forecast ${record.date}.pdf`;
            link.click();
            window.URL.revokeObjectURL(pdfUrl);
          } catch (error) {
            messageApi.error('Failed to download the alternative forecast PDF');
            console.error('Error fetching forecast PDF:', error);
          }
        };

        return (
          <div style={{ fontSize: '13px' }}>
            <div style={{ fontWeight: 500, marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '4px', color: '#1890ff' }}>
              {altForecast.station_name}
              {altForecast.pdf_available ? (
                <Tooltip title="Download alternative forecast PDF">
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined style={{ color: '#1890ff' }} />}
                    onClick={downloadPDF}
                    style={{ padding: '0 4px' }}
                  />
                </Tooltip>
              ) : (
                <Tooltip
                  title={
                    <>
                      The alternative forecast PDF is unavailable. <br />
                      Please upload the PDF to the Google Drive at: <br />
                      {altForecast.pdf_path}
                    </>
                  }
                >
                  <WarningOutlined style={{ color: 'goldenrod', fontSize: '14px' }} />
                </Tooltip>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
              <div>{sourceLabel}</div>
              <div>{altForecast.pdf_time} • {typeLabel}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Sync',
      dataIndex: 'forecast_id',
      key: 'sync',
      width: '8%',
      align: 'center',
      render: (forecast_id, record) => (
        <Tooltip title={`Sync ${record.station_name} forecast PDF with Google Drive`}>
          <Button
            type="secondary"
            shape="circle"
            size="middle"
            icon={
              syncingForecasts.includes(record.forecast_id) ? (
                <SyncOutlined spin />
              ) : (
                <SyncOutlined />
              )
            }
            onClick={() => handleForecastSync(record.forecast_id)}
            disabled={syncingForecasts.includes(record.forecast_id)}
          />
        </Tooltip>
      ),
    },
  ]

  const [tableHeight, setTableHeight] = useState('100vh');
  const titleBoxRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleBoxHeight = titleBoxRef.current ? titleBoxRef.current.offsetHeight : 32;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 48;

      const totalHeightAroundTableRows =
        headerHeight +
        titleBoxHeight +
        padding * 4 +
        tableHeaderHeight;

      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    if (selectedEvent) {
      calculateTableHeight();
    }

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, [selectedEvent]);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20', colorLinkHover: '#F57D20', colorLink: '#F57D20' } }}>
      {contextHolder}
      <Box display="flex">
        {/* Side Menu */}
        <Box sx={{ width: '250px', flexShrink: 0 }}>
          <SideMenu />
        </Box>

        {/* Main Content */}
        <Card
          style={{
            height: 'calc(100vh - 72px - 16px)',
            marginRight: '16px',
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)'
          }}
        >

          {/* Header and Event Selection */}
          <Box ref={titleBoxRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h5" component="div">
              Weather History
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                placeholder="Select Event"
                style={{ width: 320 }}
                options={allEvents.map((event) => ({
                  label: `${event.event_name} - ${event.service_type}`,
                  value: event.event_id,
                }))}
                value={selectedEventId}
                onChange={handleEventChange}
                loading={eventsLoading}
              />
            </Box>
          </Box>

          <Box ref={tableRef} sx={{ overflow: 'hidden', marginTop: '16px' }}>
            <Table
              loading={loading}
              dataSource={forecasts}
              columns={forecastColumns}
              rowKey="forecast_id"
              pagination={false}
              scroll={{ y: tableHeight }}
              locale={{
                emptyText: 'No weather stations',
              }}
            />
          </Box>
        </Card>
      </Box>
    </ConfigProvider>
  );
};

export default EventHistoryPage;
